<template>
  <div>
    
    <!-- Collection Navigation -->
    <CollectionNavigation 
      :current-collection="currentCollectionId" 
      @navigate="handleCollectionNavigate" 
    />
    
    <main class="min-h-screen bg-white">
      <!-- Collection Section -->
      <CollectionSection :collection-id="currentCollectionId" />
      
      <!-- Countdown Timer Section -->
      <CountdownTimer :message="countdownMessage" />
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '#imports'
import { useSeasons } from '~/composables/useSeasons'
import { defaultCollection, findCollectionById } from '~/data/collections'
import { useSeo, buildCollectionPageSchema, buildItemListSchema } from '~/composables/useSeo'

definePageMeta({
  layout: 'default'
})

const route = useRoute()
const { t, locale } = useI18n()
const { currentSeasonIndex } = useSeasons()
// /collection (no id) shows current season; /collection/1 etc. show that collection
const currentCollectionId = computed(() => {
  const id = parseInt(route.params.id as string)
  return Number.isFinite(id) ? id : currentSeasonIndex.value
})

const fallbackCollection = computed(() => findCollectionById(currentCollectionId.value) ?? defaultCollection)

// SEO: avoid duplicate content with /collection/[id]. Canonical = numbered URL; noindex this path.
const collectionCanonicalPath = computed(() => {
  const loc = locale?.value ?? 'en'
  const prefix = loc === 'en' ? '' : `/${loc}`
  return `${prefix}/collection/${currentSeasonIndex.value}`
})

const countdownMessage = computed(() => {
  return (
    fallbackCollection.value.countdownMessage ||
    t('pages.collection.countdown.default', { number: fallbackCollection.value.id + 1 })
  )
})

const collectionDescription = computed(() => fallbackCollection.value.description)

const BASE_URL = 'https://ommi.shop'

// Build structured data for collection page
const collectionStructuredData = computed(() => {
  const schemas = [
    buildCollectionPageSchema({
      name: `Collection #${fallbackCollection.value.id}`,
      description: collectionDescription.value,
      image: '/ommi-shop-og-image.png'
    })
  ]

  // Add ItemList schema if there are products
  if (fallbackCollection.value.products && fallbackCollection.value.products.length > 0) {
    schemas.push(
        buildItemListSchema(
        fallbackCollection.value.products.map(product => ({
          name: product.name,
          description: product.description,
          image: Array.isArray(product.images) && product.images.length > 0 
            ? product.images[0] 
            : product.image || '',
          url: `${BASE_URL}/collection#product-${product.id}`
        }))
      )
    )
  }

  return schemas
})

useSeo({
  title: t('pages.collection.meta.title', { number: fallbackCollection.value.id }),
  description: t('pages.collection.meta.description', {
        number: fallbackCollection.value.id,
        description: collectionDescription.value
  }),
  keywords: `ommi.shop collection ${fallbackCollection.value.id}, handmade, imperfect art, unique pieces, artisan marketplace`,
  type: 'website',
  structuredData: collectionStructuredData.value,
  canonicalUrl: collectionCanonicalPath.value,
  noindex: true,
})

// Navigation handler
const handleCollectionNavigate = (collectionId: number) => {
  console.log('Navigating to collection:', collectionId)
  // Navigation is handled in the component itself
}
</script>